import ManagedTable, { ManagedColumn, ManagedColumnExtra, ManagedTableProps } from 'app/common/table/ManagedTable';
import { MeasureDto } from 'app/model/CORE';
import React from 'react';
import useWindowDimensions from '../../common/hooks/useWindowDimensions';
import './SlotTable.less';
import MeasureUtils from '../../common/util/MeasureUtils';
import { CheckCircleFilled, CloseCircleOutlined } from '@ant-design/icons';

/* Measure Columns & Measure Table definitions */
export class MeasureColumn extends ManagedColumn<MeasureDto> {
    constructor(
        header: string,
        width: string,
        getCellValue: (object: MeasureDto, parent: MeasureTableBase) => any,
        extra?: ManagedColumnExtra<MeasureDto>,
    ) {
        super(header, width, getCellValue, extra);
    }
}

export const MEASURE_SELECTED_COLUMN = new MeasureColumn(
    '',
    '2em',
    object => (
        <div className={'check-circle'} style={{ textAlign: 'center' }}>
            <CheckCircleFilled />
        </div>
    ),
    { name: 'selected' },
);

export class StandardMeasureColumn {
    public static readonly ID = new MeasureColumn('ID', '7em', object => object.measureId.id);
    public static readonly REASON_KIND = new MeasureColumn('REASON/KIND', '20em', object =>
        toMeasureReasonKindString(object),
    );
    public static readonly SCENARIO = new MeasureColumn(
        'SCENARIO',
        '12em',
        object => object?.scenarioReference?.scenarioId,
    );
    public static readonly TV = new MeasureColumn('TFV', '7em', object => MeasureUtils.getTvLocation(object).id);
    public static readonly TYPE = new MeasureColumn('TYPE', '8em', object => MeasureUtils.toMeasureTypeString(object));
    public static readonly REMOVE = new MeasureColumn(
        '',
        '4em',
        (object, parent) => {
            return (
                <CloseCircleOutlined
                    className="show-on-hover"
                    style={{ fontSize: '1.5em' }}
                    onClick={e => {
                        parent.removeMeasure(object, e);
                    }}
                />
            );
        },
        {
            name: 'delete',
            className: () => 'on-hover',
        },
    );
    public static readonly ACTION = new MeasureColumn('ACTION', '6em', object =>
        MeasureUtils.isRegulationProposal(object) ? MeasureUtils.toActionShortString(object.action) : 'CREATION',
    );
}

function toMeasureReasonKindString(measure: MeasureDto): string {
    if (MeasureUtils.isRegulation(measure) || MeasureUtils.isRegulationProposal(measure)) {
        return measure.reason;
    } else if (MeasureUtils.isRerouting(measure)) {
        return measure.subType;
    }
    return '';
}

class MeasureTableBase extends ManagedTable<MeasureDto, MeasureTableProps> {
    removeMeasure = (measure: MeasureDto, event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();
        this.props.onRemoveMeasure(measure);
    };
}

export interface MeasureTableProps extends ManagedTableProps<MeasureDto> {
    onRemoveMeasure(value: MeasureDto);
}

// I have had to adapt ManagedTable so that it accepts a font size.
// VirtualTable that is behind only accepts column width in "px", whereas our column definition is in "em".
// This usually is not a problem when font size is fixed, but in our case, font size depends on the viewport width.
// When the viewport is resized, the new font size is provided to the flight table, that will adapt column width accordingly.
const MeasureTable = (props: MeasureTableProps) => {
    const { width } = useWindowDimensions();
    const fontSize = (0.42 * width) / 100;

    return <MeasureTableBase fontSize={fontSize} {...props} />;
};

export const MEASURE_LIST_COLUMNS: MeasureColumn[] = [
    MEASURE_SELECTED_COLUMN,
    StandardMeasureColumn.TYPE,
    StandardMeasureColumn.ID,
    StandardMeasureColumn.TV,
    StandardMeasureColumn.SCENARIO,
    StandardMeasureColumn.REASON_KIND,
];

export default MeasureTable;
